// src/components/POS/Receipt/DiscountSelectionModal.jsx
import React, { useState } from 'react';
import { X, Tag, Percent, IndianRupee, Gift, Search, CheckCircle, AlertCircle, Loader2, Package, TrendingUp } from 'lucide-react';

/**
 * Get icon based on discount type
 */
const getDiscountIcon = (discountType) => {
  switch (discountType) {
    case 'percentage':
      return <Percent size={16} />;
    case 'fixed':
      return <IndianRupee size={16} />;
    case 'bogo':
    case 'complimentary':
      return <Gift size={16} />;
    case 'bundle':
      return <Package size={16} />;
    case 'tiered':
      return <TrendingUp size={16} />;
    default:
      return <Tag size={16} />;
  }
};

/**
 * Parse tier slabs from config
 */
const parseTierSlabs = (tierConfig) => {
  if (!tierConfig) return [];
  try {
    const parsed = typeof tierConfig === 'string' ? JSON.parse(tierConfig) : tierConfig;
    return Array.isArray(parsed) ? parsed.sort((a, b) => (a.minAmount || 0) - (b.minAmount || 0)) : [];
  } catch { return []; }
};

/**
 * Format discount value for display
 */
const formatDiscountValue = (discount) => {
  const { discountType, discountValue, buyQuantity, getQuantity, bundlePrice } = discount;

  switch (discountType) {
    case 'percentage':
      return `${discountValue}% off`;
    case 'fixed':
      return `₹${discountValue} off`;
    case 'bogo':
      if (buyQuantity === 1 && getQuantity === 1) {
        return 'Buy 1 Get 1 Free';
      }
      return `Buy ${buyQuantity} Get ${getQuantity} Free`;
    case 'complimentary':
      return `Buy ${buyQuantity || 1} Get ${getQuantity || 1} Free`;
    case 'bundle':
      return `Combo @ ₹${bundlePrice || 0}`;
    case 'tiered': {
      const slabs = parseTierSlabs(discount.tierConfig);
      if (slabs.length > 0) {
        return slabs.map(s => `${s.value}%`).join(' / ') + ' off';
      }
      return 'Tiered Discount';
    }
    default:
      return '';
  }
};

/**
 * DiscountSelectionModal - Modal to browse and select discounts
 */
export default function DiscountSelectionModal({
  isOpen,
  onClose,
  suggestions = [],
  loading = false,
  onSelectDiscount,
  onValidateCode,
  validating = false,
  calculateSaving = () => 0,
}) {
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  if (!isOpen) return null;

  // Filter suggestions based on search
  const filteredSuggestions = suggestions.filter((d) =>
    d.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort: eligible first, then by discount value
  const sortedSuggestions = [...filteredSuggestions].sort((a, b) => {
    if (a.eligible !== b.eligible) return a.eligible ? -1 : 1;
    return (b.discountValue || 0) - (a.discountValue || 0);
  });

  const handleApplyCode = async () => {
    if (!codeInput.trim()) return;
    setCodeError(null);
    const result = await onValidateCode(codeInput.trim().toUpperCase());
    if (result.valid && result.discount) {
      onSelectDiscount(result.discount);
      onClose();
    } else {
      setCodeError(result.message || 'Invalid code');
    }
  };

  const handleSelectFromList = (discount) => {
    if (discount.eligible !== false) {
      onSelectDiscount(discount);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-5 py-4 flex justify-between items-center flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Tag size={22} />
            </div>
            <div>
              <h2 className="text-lg font-bold">Apply Discount</h2>
              <p className="text-xs text-teal-100">{suggestions.length} discounts available</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Promo Code Input */}
        <div className="px-4 pt-4 pb-2 border-b border-slate-200 bg-slate-50">
          <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Have a promo code?</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value.toUpperCase());
                  setCodeError(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyCode()}
                placeholder="Enter code"
                className="w-full pl-9 pr-3 py-2.5 text-sm font-medium border border-slate-300 rounded-lg
                  focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleApplyCode}
              disabled={!codeInput.trim() || validating}
              className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-all flex items-center gap-2
                ${!codeInput.trim() || validating
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-teal-600 text-white hover:bg-teal-700 shadow-sm'
                }
              `}
            >
              {validating ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
            </button>
          </div>
          {codeError && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-red-600">
              <AlertCircle size={12} />
              <span>{codeError}</span>
            </div>
          )}
        </div>

        {/* Search/Filter */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search discounts..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg
                focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        </div>

        {/* Discount List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent mx-auto mb-3"></div>
              <p className="text-sm text-slate-500">Loading discounts...</p>
            </div>
          ) : sortedSuggestions.length === 0 ? (
            <div className="py-8 text-center text-slate-400">
              <Tag size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">No discounts found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedSuggestions.map((discount) => {
                const isSelectable = discount.eligible !== false;
                const saving = calculateSaving(discount);

                return (
                  <button
                    key={discount.id || discount.code}
                    onClick={() => handleSelectFromList(discount)}
                    disabled={!isSelectable}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-all
                      ${isSelectable
                        ? 'bg-white border-slate-200 hover:border-teal-400 hover:shadow-md cursor-pointer'
                        : 'bg-slate-50 border-slate-100 opacity-60 cursor-not-allowed'
                      }
                    `}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`mt-0.5 w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                        ${isSelectable ? 'bg-teal-100 text-teal-600' : 'bg-slate-200 text-slate-400'}
                      `}>
                        {getDiscountIcon(discount.discountType)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded
                            ${isSelectable ? 'bg-teal-600 text-white' : 'bg-slate-300 text-slate-500'}
                          `}>
                            {discount.code}
                          </span>
                          <span className={`text-sm font-semibold ${isSelectable ? 'text-slate-800' : 'text-slate-400'}`}>
                            {formatDiscountValue(discount)}
                          </span>
                        </div>

                        <p className={`text-sm ${isSelectable ? 'text-slate-600' : 'text-slate-400'}`}>
                          {discount.name}
                        </p>

                        {/* Tier slabs detail */}
                        {discount.discountType === 'tiered' && (() => {
                          const slabs = parseTierSlabs(discount.tierConfig);
                          return slabs.length > 0 ? (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {slabs.map((slab, idx) => (
                                <span key={idx} className={`text-[10px] px-1.5 py-0.5 rounded ${isSelectable ? 'bg-indigo-50 text-indigo-600 border border-indigo-100' : 'bg-slate-100 text-slate-400'}`}>
                                  ≥₹{slab.minAmount} → {slab.value}%
                                </span>
                              ))}
                            </div>
                          ) : null;
                        })()}

                        {/* Min bill amount */}
                        {discount.minBillAmount > 0 && (
                          <p className="text-xs text-slate-400 mt-1">
                            Min. order: ₹{discount.minBillAmount}
                          </p>
                        )}

                        {/* Eligibility message for ineligible */}
                        {!isSelectable && discount.eligibilityMessage && (
                          <div className="flex items-center gap-1 mt-1.5 text-xs text-amber-600">
                            <AlertCircle size={12} />
                            <span>{discount.eligibilityMessage}</span>
                          </div>
                        )}
                      </div>

                      {/* Saving badge */}
                      {isSelectable && saving > 0 && (
                        <div className="flex-shrink-0 text-right">
                          <div className="bg-green-100 text-green-700 px-2 py-1 rounded-lg">
                            <div className="text-xs font-medium">Save</div>
                            <div className="text-sm font-bold">₹{saving.toFixed(0)}</div>
                          </div>
                        </div>
                      )}

                      {/* Arrow indicator for selectable */}
                      {isSelectable && (
                        <CheckCircle size={20} className="text-slate-300 flex-shrink-0 mt-2" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded-lg transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
